import { X, Search, ArrowLeft } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useDialog } from '../hooks/useDialog'
import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { useNavigate } from 'react-router-dom'

// ── Shared UI primitives ──────────────────────────────────────────────────────
// One home for the small chrome that was re-implemented inline across dozens of
// files (close buttons with three different glyphs, ad-hoc primary buttons, a
// dozen empty-state layouts). Prefer these in new code so the app feels uniform
// and a restyle is a one-file change.

// Spinner — the canonical inline loading indicator: the same amber ring used inside
// Button/PrimaryButton's `loading` state (UI.jsx below), broken out standalone for the
// ~dozen places that needed a spinner outside a button (a JoinRoom/Invite full-page wait,
// an inline "thinking" glyph in ForYou/Login/ScreenshotImport). Those had each reached for
// lucide's <Loader> icon instead — a generic circle-of-dashes any app ships, not the ring
// this app already uses everywhere else (Jason 2026-08-16: "not our new and consistent
// one"). Same visual language as FilmReelSpinner (App.jsx's route-level LazyFallback) at
// a scale the reel's engraved detail can't survive — this is the small/inline half of
// that pair, not a competing third style.
export function Spinner({ size = 16, color = 'var(--amber)', track = 'var(--border)', style }) {
  return (
    <span aria-hidden="true" style={{
      display: 'inline-block', width: size, height: size, borderRadius: 'var(--radius-full)',
      border: `${Math.max(2, Math.round(size * 0.15))}px solid ${track}`, borderTopColor: color,
      flexShrink: 0, '--nxa': 'spin 0.9s linear infinite', ...style,
    }} />
  )
}

// CloseButton — the canonical dismiss control. Always a lucide X (not × vs ✕),
// circular, with an aria-label. variant 'surface' for sheets, 'overlay' for use
// over a dark cinematic/backdrop.
// variants:
//   surface — the default chip: circle, --surface, bordered. For sheets and cards.
//   overlay — same shape on a dark/cinematic backdrop.
//   bare    — JUST the icon, no circle/border/background. This is not a new invention:
//             it's what several sheets (PageHelp, RewatchSheet) already draw by hand, so
//             giving it a variant lets them adopt the component with ZERO visual change
//             instead of being forced into a circle they never had. Extending the
//             component to match real usage beats making 15 call sites change appearance
//             (UI audit follow-up 2026-07-26).
export function CloseButton({ onClick, size = 32, variant = 'surface', label = 'Close', style }) {
  const bare = variant === 'bare'
  const bg = variant === 'overlay' ? 'rgba(0,0,0,0.45)' : 'var(--surface)'
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className={size < 44 ? 'ui-hit' : undefined}
      style={bare ? {
        background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer',
        padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, fontFamily: 'var(--font)', ...style,
      } : {
        width: size, height: size, borderRadius:'var(--radius-full)', background: bg,
        border: '1px solid var(--border)', color: 'var(--text-dim)', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        padding: 0, fontFamily: 'var(--font)', backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)', ...style,
      }}
    >
      <X size={bare ? size : Math.round(size * 0.5)} strokeWidth={2.2} />
    </button>
  )
}

// PrimaryButton — the canonical amber call-to-action (Track 3: one CTA to rule the
// app's gold). Uses the --grad-amber fill + --amber-ink tokens, a `loading` spinner
// state, and a press-scale (.ui-primary:active) so every primary action feels the
// same. disabled/loading dim to surface.
export function PrimaryButton({ children, onClick, disabled, loading, full, style, ...rest }) {
  // Delegates to Button variant="primary" so there is exactly ONE canonical primary.
  // It used to be its own implementation (13px padding, --radius 12, no top highlight),
  // which meant the component standard contained the very drift it exists to prevent
  // (UI audit 2026-07-26). Keeps its own API: `loading` renders the spinner, `full` spans.
  const off = disabled || loading
  return (
    <Button
      variant="primary" disabled={off} onClick={onClick}
      style={{ width: full ? '100%' : undefined, ...style }}
      {...rest}>
      {loading && (
        <span aria-hidden="true" className="ui-primary-spin" style={{
          width: 15, height: 15, borderRadius: '50%', flexShrink: 0,
          // The loading state swaps the fill to --surface, so an --amber-ink (near-black)
          // spinner was invisible dark-on-dark — it looked like a dead button. Amber reads
          // on the disabled surface and signals "working", not "broken".
          border: '2.5px solid rgba(var(--amber-rgb),0.25)', borderTopColor: 'var(--amber)',
        }}/>
      )}
      {children}
    </Button>
  )
}

// ── Button — the canonical control ladder ─────────────────────────────────────
// One component for every action in the app, so a restyle is a one-file change and the
// five rules stay enforced. Reach for <Button size="sm" pill> for inline actions
// (Follow, +Add) rather than inventing a pill — that is how drift started.
//
// Rule 2 — depth without a new colour. Three stacked layers make a filled control read
// as a lit physical surface instead of a flat rectangle:
//   1. a bright inset top highlight (the light source)
//   2. a 1px dark bottom edge (the shadow it casts on itself)
//   3. a wide, tight-to-the-button coloured drop shadow (it floats off the page)
// (Claude Design "UI component standardization" 1a, adopted 2026-07-26.)
const INSET_HI = 'inset 0 1px 0 rgba(var(--ink-rgb),0.14)'   // subtle — surfaces/tonal
const PRIMARY_DEPTH = [
  'inset 0 1px 0 rgba(var(--ink-rgb),0.45)',
  '0 1px 0 rgba(0,0,0,0.5)',
  '0 12px 28px -12px rgba(var(--amber-rgb),0.75)',
].join(', ')
const BTN_SIZES = {
  sm: { padding: '6px 14px', fontSize: 11.5, fontWeight: 700, gap: 5 },
  md: { padding: '12px 18px', fontSize: 14,   fontWeight: 600, gap: 8 },
  lg: { padding: '14px 22px', fontSize: 15,   fontWeight: 700, gap: 8 },
}

// `accent` (2026-08-15, the one open design call from the UI-debt plan): a CSS color
// ('var(--purple)', 'var(--green)', a hex) that recolors the primary and tonal
// variants — the reason ~25 purple/green CTAs (CatchMeUp, RewatchSheet, Admin…) sat
// hand-rolled was that the ladder could only speak amber. Secondary/ghost/destructive
// ignore it (they carry no accent by design). Accented primaries skip the .ui-primary
// class — its halo is amber in CSS and would clash — and bake an equivalent glow into
// the box-shadow instead.
export function Button({ variant = 'primary', accent, size, pill = false, children, style, disabled, ...props }) {
  const base = {
    fontFamily: 'var(--font)', cursor: disabled ? 'default' : 'pointer',
    borderRadius: 'var(--radius-md)',   // rule 1: 14 by default; pill overrides below
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  }
  let v
  if (variant === 'secondary') {
    // Raised glass: a soft top-to-bottom sheen + inset highlight so it reads as a real
    // surface, not a flat outline (rule 2).
    v = { background: 'linear-gradient(180deg, rgba(var(--ink-rgb),0.10), rgba(var(--ink-rgb),0.05))',
      border: '1px solid rgba(var(--ink-rgb),0.14)', color: 'var(--text-strong)',
      fontWeight: 600, fontSize: 14, padding: '12px 18px', boxShadow: 'inset 0 1px 0 rgba(var(--ink-rgb),0.09)' }
  } else if (variant === 'tonal') {
    // The second-loudest action: accent tint, no glow — reserves the glow for one CTA (rule 3).
    v = accent
      ? { background: `linear-gradient(180deg, color-mix(in srgb, ${accent} 14%, transparent), color-mix(in srgb, ${accent} 8%, transparent))`,
          border: `1px solid color-mix(in srgb, ${accent} 26%, transparent)`,
          color: accent, fontWeight: 700, fontSize: 14, padding: '12px 18px',
          boxShadow: INSET_HI }
      : { background: 'linear-gradient(180deg, rgba(var(--amber-rgb),0.14), rgba(var(--amber-rgb),0.08))',
          border: '1px solid rgba(var(--amber-rgb),0.26)',
          color: 'var(--amber)', fontWeight: 700, fontSize: 14, padding: '12px 18px',
          boxShadow: INSET_HI }
  } else if (variant === 'ghost') {
    v = { background: 'none', border: '1px solid transparent', color: 'var(--text-dim)', fontWeight: 600, padding: '11px 14px' }
  } else if (variant === 'destructive') {
    // Rule 5: outlined, never a filled red block. Softened text (#ff8b7f) so it doesn't
    // shout on a dark surface; the outline carries the danger signal.
    v = { background: 'transparent', border: '1px solid rgba(231,76,60,0.42)', color: 'var(--danger-text)',
      fontWeight: 700, fontSize: 14, padding: '12px 18px' }
  } else { // primary — the one filled CTA (amber unless accented)
    v = disabled
      ? { background: 'var(--surface)', color: 'var(--text-dim)', border: 'none',
          padding: '14px 22px', fontWeight: 700, fontSize: 15 }
      : accent
      ? { background: `linear-gradient(135deg, color-mix(in srgb, ${accent} 82%, #fff), ${accent})`,
          color: 'var(--amber-ink)', border: 'none',
          padding: '14px 22px', fontWeight: 700, fontSize: 15,
          letterSpacing: '-0.01em',
          // Same 3-layer depth recipe as PRIMARY_DEPTH, glow keyed to the accent.
          boxShadow: `inset 0 1px 0 rgba(var(--ink-rgb),0.45), 0 1px 0 rgba(0,0,0,0.5), 0 12px 28px -12px color-mix(in srgb, ${accent} 75%, transparent)` }
      : { background: 'var(--grad-amber)', color: 'var(--amber-ink)', border: 'none',
          padding: '14px 22px', fontWeight: 700, fontSize: 15,
          letterSpacing: '-0.01em',   // tightens the label so a bold amber slab reads crisp
          boxShadow: PRIMARY_DEPTH }
  }
  // Primary variant opts into the shared .ui-primary halo + press-scale so every
  // canonical primary CTA glows the same way (Jason 2026-07-20). Caller className wins.
  // Accented primaries skip it — the halo is amber in CSS (see comment above Button).
  const cls = variant === 'primary' && !disabled && !accent ? 'ui-primary' : undefined
  // size overrides the variant's default padding/scale; pill forces the stadium radius
  // (never --radius-full, which is 50% and ellipses a non-square box).
  const sizing = size ? BTN_SIZES[size] || BTN_SIZES.md : null
  const shape  = pill ? { borderRadius: 'var(--radius-pill)' } : null
  return <button disabled={disabled} className={cls} style={{ ...base, ...v, ...sizing, ...shape, ...style }} {...props}>{children}</button>
}

// Chip — the canonical pill toggle (filters, genres, service selectors). Default is
// the amber tint; pass `color` (a brand hex like a streaming service) to tint the
// active state with that brand instead. Merge `style` for layout/weight per caller.
export function Chip({ active, color, children, style, ...props }) {
  const bg = color ? (active ? `${color}22` : 'var(--bg-card)')
                    : (active ? 'rgba(var(--amber-rgb),0.12)' : 'var(--bg-card)')
  // Inactive chips get their OWN border, brighter than --border (0.09 white), which is
  // tuned for large card edges and all but vanishes around a 12px pill — the metadata
  // chips read as floating text rather than pills (Jason 2026-07-27).
  //
  // Raised contrast rather than thickness: at this size a 2px stroke reads as a heavy
  // outline and fights the poster art behind it, while doubling the alpha makes the shape
  // legible at the same weight. Say the word if you want it thicker instead.
  const CHIP_EDGE = 'rgba(var(--ink-rgb),0.20)'
  const bd = color ? (active ? color : CHIP_EDGE)
                    : (active ? 'rgba(var(--amber-rgb),0.3)' : CHIP_EDGE)
  const fg = color ? (active ? color : 'var(--text-muted)')
                    : (active ? 'var(--amber)' : 'var(--text-muted)')
  return (
    <button style={{
      borderRadius: 'var(--radius-pill)', padding: '5px 12px', fontSize: 12,
      fontFamily: 'var(--font)', cursor: 'pointer', whiteSpace: 'nowrap',
      border: `1px solid ${bd}`, background: bg, color: fg, ...style,
    }} {...(active !== undefined ? { 'aria-pressed': !!active } : {})} {...props}>{children}</button>
  )
}

// ── Typography primitives ─────────────────────────────────────────────────────
// The canonical text scale (Outfit): body 400/500, titles 800, eyebrows 700 at one
// letter-spacing (0.08em), stat numbers 800. Prefer these in new code so weights and
// spacing stay consistent instead of being re-picked inline on every surface.

// Eyebrow — the small uppercase kicker above a title. One weight, one letter-spacing.
// 17: mono, tracked, amber by default — the same eyebrow every 17 page hand-rolls.
export function Eyebrow({ children, color = 'var(--amber)', size = 10.5, style }) {
  return <div style={{ fontFamily: 'var(--font-mono)', fontSize: size, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color, ...style }}>{children}</div>
}

// SectionTitle — the canonical card/section/modal title.
// 17: every title is the serif at weight 500; `serif` is kept for callers but is a no-op.
export function SectionTitle({ children, size = 18, serif = true, color = 'var(--text-strong)', style }) {
  void serif
  return <div style={{ fontFamily: 'var(--font-serif)', fontSize: size, fontWeight: 500, letterSpacing: '-0.01em', color, lineHeight: 1.2, ...style }}>{children}</div>
}

// StatNumber — a big numeric stat, serif + tabular figures so digits don't jitter.
export function StatNumber({ children, size = 24, color = 'var(--text-strong)', style }) {
  return <div style={{ fontFamily: 'var(--font-serif)', fontSize: size, fontWeight: 500, fontVariantNumeric: 'tabular-nums', lineHeight: 1, color, ...style }}>{children}</div>
}

// EmptyState — canonical "nothing here yet" block. Pass an emoji or a custom icon
// node, a title, optional subtitle, and an optional action element (e.g. a button).
// The emoji sits in a soft glow badge (matches the notification/celebration
// language elsewhere) with a gentle entrance + idle bob, so "nothing here" reads
// as a designed beat instead of leftover placeholder copy (Jason 2026-07-03: empty
// states were "completely generic, zero personality" — the weakest UX surface).
export function EmptyState({ emoji, icon, title, subtitle, action, accent = 'var(--amber)', style }) {
  return (
    <div style={{ textAlign: 'center', padding: '52px 24px', fontFamily: 'var(--font)', '--nxa': 'emptyStateIn 0.5s var(--ease-out) both', ...style }}>
      {icon ? <div style={{ marginBottom: 14 }}>{icon}</div>
        : emoji ? (
          <div style={{
            width: 64, height: 64, borderRadius:'var(--radius-xl)', margin: '0 auto 16px',
            background: `color-mix(in srgb, ${accent} 12%, transparent)`,
            border: `1px solid color-mix(in srgb, ${accent} 28%, transparent)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, lineHeight: 1, '--nxa': 'emptyBadgeBob 3.2s ease-in-out 0.4s infinite',
          }}>{emoji}</div>
        ) : null}
      {title && <div style={{ color: 'var(--text-strong)', fontWeight: 700, fontSize: 16, marginBottom: 6 }}>{title}</div>}
      {subtitle && <div style={{ color: 'var(--text-dim)', fontSize: 13, lineHeight: 1.55, maxWidth: 320, margin: '0 auto' }}>{subtitle}</div>}
      {action && <div style={{ marginTop: 18 }}>{action}</div>}
    </div>
  )
}

// ── Form + navigation primitives (component standard, card 1c/1d/1e/2a/2b) ──────
// These encode the proposed control set so new screens compose them instead of
// re-inventing a floating label, a sliding thumb, or an anchored menu each time.

// TextField — the canonical text input (1c): a floating label that shrinks to an
// uppercase kicker on focus/fill, an amber focus ring, and an inline error state.
// fontSize stays 16 so iOS never zoom-scrolls the field. Pass `as="textarea"` for
// notes, or `trailing` for a right-aligned adornment (password eye, ⌘K hint).
// Uncontrolled-friendly: pass value/onChange to control it.
export function TextField({ label, value, onChange, error, as, rows = 3, trailing, style, ...props }) {
  const [focus, setFocus] = useState(false)
  const filled = focus || (value != null && String(value).length > 0)
  const Tag = as === 'textarea' ? 'textarea' : 'input'
  const ring = error ? 'rgba(231,76,60,0.55)' : focus ? 'rgba(var(--amber-rgb),0.65)' : 'var(--border)'
  return (
    <div style={{ ...style }}>
      <label style={{
        display: 'block', position: 'relative',
        background: focus ? 'rgba(var(--ink-rgb),0.06)' : 'var(--bg-card)',
        border: `1px solid ${ring}`, borderRadius: 'var(--radius-md)',
        padding: label ? '20px 14px 9px' : '13px 14px',
        boxShadow: focus && !error ? '0 0 0 3px rgba(var(--amber-rgb),0.16)' : 'none',
        transition: 'border-color 160ms var(--ease-out), box-shadow 160ms var(--ease-out), background 160ms var(--ease-out)',
      }}>
        {label && (
          <span style={{
            position: 'absolute', left: 14, top: filled ? 8 : 17, pointerEvents: 'none',
            fontSize: filled ? 10 : 15, fontWeight: filled ? 700 : 500,
            letterSpacing: filled ? '0.08em' : 0, textTransform: filled ? 'uppercase' : 'none',
            color: error ? 'var(--danger-text)' : focus ? 'var(--amber)' : 'var(--text-dim)',
            transition: 'all 170ms var(--ease-out)',
          }}>{label}</span>
        )}
        <Tag
          value={value} onChange={onChange}
          onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
          rows={as === 'textarea' ? rows : undefined}
          placeholder={label ? ' ' : props.placeholder}
          style={{
            width: '100%', background: 'transparent', border: 'none', outline: 'none',
            color: 'var(--text-strong)', fontSize: 16, fontWeight: 500, padding: 0,
            paddingRight: trailing ? 30 : 0,
            fontFamily: 'var(--font)', resize: as === 'textarea' ? 'none' : undefined,
            lineHeight: as === 'textarea' ? 1.5 : undefined,
          }}
          {...props}
        />
        {trailing && (
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>{trailing}</span>
        )}
      </label>
      {error && typeof error === 'string' && (
        <div style={{ color: 'var(--danger-text)', fontSize: 11.5, marginTop: 6, fontFamily: 'var(--font)' }}>{error}</div>
      )}
    </div>
  )
}

// SegmentedControl — 2-4 mutually exclusive options with an amber thumb that slides
// on --ease-out; labels never move (1e). Replaces hand-rolled tab rows. `options` is
// [{value,label}] (or plain strings); `value`/`onChange` control the selection.
export function SegmentedControl({ options, value, onChange, style }) {
  const opts = options.map(o => (typeof o === 'string' ? { value: o, label: o } : o))
  const idx = Math.max(0, opts.findIndex(o => o.value === value))
  return (
    <div role="tablist" style={{
      position: 'relative', display: 'flex', background: 'var(--bg-card)',
      border: '1px solid var(--border-soft)', borderRadius: 'var(--radius-md)', padding: 4, ...style,
    }}>
      <span aria-hidden="true" style={{
        position: 'absolute', top: 4, bottom: 4,
        left: `calc(4px + ${idx} * ((100% - 8px) / ${opts.length}))`,
        width: `calc((100% - 8px) / ${opts.length})`, borderRadius: 'var(--radius-sm)',
        background: 'var(--grad-amber)',
        boxShadow: 'inset 0 1px 0 rgba(var(--ink-rgb),0.4), 0 6px 16px -8px rgba(var(--amber-rgb),0.8)',
        transition: 'left 300ms var(--ease-out)',
      }} />
      {opts.map((o, i) => (
        <button key={o.value} role="tab" aria-selected={i === idx} onClick={() => onChange?.(o.value)} style={{
          position: 'relative', flex: 1, background: 'none', border: 'none', cursor: 'pointer',
          padding: '9px 0', fontSize: 13, fontWeight: 700, borderRadius: 'var(--radius-sm)',
          fontFamily: 'var(--font)', transition: 'color 200ms var(--ease-out)',
          color: i === idx ? 'var(--amber-ink)' : 'var(--text-dim)',
        }}>{o.label}</button>
      ))}
    </div>
  )
}

// MagneticToggle — replaces SegmentedControl where a little personality earns its
// keep (design sync 2026-08-14): same sliding-thumb shape, but the thumb
// OVERSHOOTS past the target before settling, like a magnet snapping into place.
// Drop-in for SegmentedControl's exact API — same options/value/onChange.
export function MagneticToggle({ options, value, onChange, style }) {
  const opts = options.map(o => (typeof o === 'string' ? { value: o, label: o } : o))
  const idx = Math.max(0, opts.findIndex(o => o.value === value))
  return (
    <div role="tablist" style={{
      position: 'relative', display: 'flex', background: 'var(--bg-card)',
      border: '1px solid var(--border-soft)', borderRadius: 'var(--radius-md)', padding: 4, ...style,
    }}>
      <span aria-hidden="true" style={{
        position: 'absolute', top: 4, bottom: 4,
        left: `calc(4px + ${idx} * ((100% - 8px) / ${opts.length}))`,
        width: `calc((100% - 8px) / ${opts.length})`, borderRadius: 'var(--radius-sm)',
        background: 'var(--grad-amber)',
        boxShadow: 'inset 0 1px 0 rgba(var(--ink-rgb),0.4), 0 6px 16px -8px rgba(var(--amber-rgb),0.8)',
        // The overshoot: a spring-style cubic-bezier that swings past 100% before
        // settling, instead of SegmentedControl's plain ease-out slide.
        transition: 'left 420ms cubic-bezier(0.34,1.56,0.64,1)',
      }} />
      {opts.map((o, i) => (
        <button key={o.value} role="tab" aria-selected={i === idx} onClick={() => onChange?.(o.value)} style={{
          position: 'relative', flex: 1, background: 'none', border: 'none', cursor: 'pointer',
          padding: '9px 0', fontSize: 13, fontWeight: 700, borderRadius: 'var(--radius-sm)',
          fontFamily: 'var(--font)', transition: 'color 260ms ease',
          color: i === idx ? 'var(--amber-ink)' : 'var(--text-dim)',
        }}>{o.label}</button>
      ))}
    </div>
  )
}

// PillTabs — the recommended horizontal nav (2b): a filled amber pill marks the active
// tab, overflow scrolls (a half-cut pill is self-evidently scrollable — no mask/chevron).
// `tabs` is [{value,label,count}]; counts render as an inline badge when present.
export function PillTabs({ tabs, value, onChange, style }) {
  return (
    <div role="tablist" style={{
      display: 'flex', gap: 2, overflowX: 'auto', padding: 4,
      background: 'var(--bg-card)', border: '1px solid var(--border-soft)',
      borderRadius: 'var(--radius-pill)', scrollbarWidth: 'none', ...style,
    }}>
      {tabs.map(t => {
        const on = t.value === value
        return (
          <button key={t.value} role="tab" aria-selected={on} onClick={() => onChange?.(t.value)} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, whiteSpace: 'nowrap',
            cursor: 'pointer', border: 'none', borderRadius: 'var(--radius-pill)', padding: '8px 15px',
            fontSize: 13, fontWeight: 700, fontFamily: 'var(--font)',
            background: on ? 'var(--grad-amber)' : 'transparent',
            color: on ? 'var(--amber-ink)' : 'var(--text-dim)',
            boxShadow: on ? 'inset 0 1px 0 rgba(var(--ink-rgb),0.4), 0 6px 16px -8px rgba(var(--amber-rgb),0.8)' : 'none',
            transition: 'background 200ms var(--ease-out), color 200ms var(--ease-out)',
          }}>
            {t.label}
            {t.count != null && (
              <span style={{
                fontSize: 10, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '1px 6px',
                background: on ? 'rgba(var(--bg-rgb),0.18)' : 'rgba(var(--ink-rgb),0.08)',
                color: on ? 'var(--amber-ink)' : 'var(--text-dim)',
              }}>{t.count}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// Tabs — the canonical underline tab bar: a single amber bar that SLIDES to the
// active tab (spring ease + soft glow). Extracted from ShowDetail's hand-rolled
// spotlight underline (the design mock's tab treatment) so every content-switching
// tab row in the app shares the one behavior — this is for true tab bars (content
// switchers); PillTabs/SegmentedControl remain the pill-style variants.
// `tabs`: strings or { value, label, badge } — badge renders as a small count chip.
// `stretch` makes tabs share the row equally (mock style); default hugs content.
export function Tabs({ tabs, value, onChange, stretch = false, size = 13, style }) {
  const opts = tabs.map(t => (typeof t === 'string' ? { value: t, label: t } : t))
  const refs = useRef({})
  const rowRef = useRef(null)
  const [bar, setBar] = useState({ left: 0, width: 0 })
  // Measure the active tab whenever it changes, a badge changes its width, or the
  // row itself resizes (rotation, font swap-in) — the underline must track exactly.
  const badgeKey = opts.map(o => o.badge ?? '').join('|')
  useLayoutEffect(() => {
    const measure = () => {
      const el = refs.current[value]
      if (el) setBar({ left: el.offsetLeft, width: el.offsetWidth })
    }
    measure()
    // Long rows scroll (overflowX below) — keep the active tab in view when it
    // changes, the same courtesy Profile's hand-rolled bar extended. Only when the
    // row actually overflows: scrollIntoView scrolls EVERY ancestor, so on a
    // non-overflowing bar it could yank the page vertically at mount
    // (block:'nearest' still scrolls the document, audit 2026-08-14).
    try {
      const row = rowRef.current
      if (row && row.scrollWidth > row.clientWidth + 1)
        refs.current[value]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    } catch { /* noop */ }
    const ro = typeof ResizeObserver !== 'undefined' && rowRef.current ? new ResizeObserver(measure) : null
    if (ro) ro.observe(rowRef.current)
    return () => { if (ro) ro.disconnect() }
  }, [value, badgeKey, opts.length])
  return (
    <div ref={rowRef} role="tablist" style={{
      position: 'relative', display: 'flex', gap: 2, borderBottom: '1px solid var(--border)',
      overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', ...style,
    }}>
      <div aria-hidden style={{
        position: 'absolute', bottom: -1, height: 2, borderRadius: 2, background: 'var(--amber)',
        boxShadow: '0 0 8px rgba(var(--amber-rgb),0.6)', left: bar.left, width: bar.width,
        transition: 'left 0.28s cubic-bezier(0.34,1.15,0.64,1), width 0.28s cubic-bezier(0.34,1.15,0.64,1)',
        opacity: bar.width ? 1 : 0, pointerEvents: 'none',
      }} />
      {opts.map(o => {
        const on = o.value === value
        return (
          <button key={o.value} role="tab" aria-selected={on}
            ref={el => { refs.current[o.value] = el }} onClick={() => onChange?.(o.value)}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'var(--font)',
              color: on ? 'var(--amber)' : 'var(--text-muted)', fontWeight: on ? 700 : 500, fontSize: size,
              padding: '8px 14px', transition: 'color 0.2s', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 5, ...(stretch ? { flex: 1 } : {}),
            }}>
            {o.label}
            {o.badge != null && o.badge !== 0 && (
              <span style={{
                background: on ? 'rgba(var(--amber-rgb),0.25)' : 'var(--surface)',
                color: on ? 'var(--amber)' : 'var(--text-muted)',
                borderRadius: 'var(--radius-xs)', padding: '1px 5px', fontSize: 9, fontWeight: 700, lineHeight: '14px',
              }}>{o.badge}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// AdjustCard — the "adjust" expandable from the ShowDetail design mock: a bordered
// card whose header row is always visible (icon/visual + title + sub + a trailing
// hint), with a chevron that PIVOTS from horizontal (›) to vertical when opened and
// the body expanding in place via the house <Collapse>. Use it wherever a summary
// row hides richer controls (episode-position picker, Start-a-Run pace, etc.).
// `accent` tints the border/chevron (defaults to neutral); `visual` is an optional
// leading node (progress ring, emoji); `hint` is the trailing label ("Adjust").
export function AdjustCard({ open, onToggle, visual, title, sub, hint, accent, children, style }) {
  const line = accent ? `color-mix(in srgb, ${accent} 30%, transparent)` : 'var(--border)'
  return (
    <div style={{
      background: accent ? `color-mix(in srgb, ${accent} 6%, var(--bg-card))` : 'var(--bg-card)',
      border: `1px solid ${line}`, borderRadius: 'var(--radius-md)', overflow: 'hidden', ...style,
    }}>
      <button onClick={onToggle} aria-expanded={!!open} style={{
        width: '100%', background: 'none', border: 'none', padding: '10px 12px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 11, fontFamily: 'var(--font)', textAlign: 'left',
      }}>
        {visual != null && <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{visual}</span>}
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', color: 'var(--text-strong)', fontWeight: 700, fontSize: 13 }}>{title}</span>
          {sub && <span style={{ display: 'block', color: 'var(--text-dim)', fontSize: 11, marginTop: 1 }}>{sub}</span>}
        </span>
        <span style={{
          flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4,
          color: accent || 'var(--text-dim)', fontSize: 11.5, fontWeight: 700,
        }}>
          {hint}
          <span aria-hidden style={{
            display: 'inline-block', fontSize: 15, lineHeight: 1,
            transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.25s var(--ease-out)',
          }}>›</span>
        </span>
      </button>
      <Collapse open={!!open}>
        <div style={{ padding: '2px 12px 14px', borderTop: `1px solid ${line}` }}>
          {children}
        </div>
      </Collapse>
    </div>
  )
}

// FilmstripTabs — replaces PillTabs at CONTENT-tab call sites (design sync
// 2026-08-14, "Interaction proposals"). Three sprocket ticks mark the active tab
// instead of a filled pill; pair with <FilmstripPanel> to slide the body in the
// direction tapped, like changing reels. Filter-style PillTabs usage (counts,
// dense rows) is unaffected — this is specifically for the About/Episodes/
// Related-style switcher.
// `tabs`: strings or { value, label, badge }. `stretch`/`size` mirror <Tabs> so
// existing call sites swap in without losing layout/badge behavior.
export function FilmstripTabs({ tabs, value, onChange, stretch = false, size = 14, style }) {
  // 17: the sprocket strip is gone; every content switcher is the same amber pill row
  // the 17 pages hand-roll (Discover, My List, Tonight, People). Same props, so the
  // remaining call sites (Cliffhanger, the bell) swap in without edits.
  // 17.144: the amber pill is ONE element that slides to the chosen tab (measured from
  // the tab buttons, re-measured on resize); the buttons themselves stay transparent.
  const opts = tabs.map(t => (typeof t === 'string' ? { value: t, label: t } : t))
  const rowRef = useRef(null)
  const [pill, setPill] = useState(null)
  useLayoutEffect(() => {
    const row = rowRef.current
    if (!row) return
    const measure = () => {
      const el = row.querySelector('[role="tab"][aria-selected="true"]')
      if (!el) { setPill(null); return }
      setPill({ x: el.offsetLeft, w: el.offsetWidth })
    }
    measure()
    let ro
    try { ro = new ResizeObserver(measure); ro.observe(row) } catch {}
    return () => ro?.disconnect()
  }, [value, opts.length, stretch])
  return (
    <div ref={rowRef} role="tablist" className="ui-tabs" style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', paddingBottom: 2, ...style }}>
      {pill && <span aria-hidden="true" className="ui-tabs-pill" style={{ width: pill.w, transform: `translateX(${pill.x}px)` }} />}
      {opts.map(o => {
        const on = o.value === value
        return (
          <button key={o.value} role="tab" aria-selected={on} onClick={() => onChange?.(o.value)} className={`ui-hit${on ? ' ui-tab-on' : ''}`} {...(on ? { 'data-friction-ignore': '' } : {})} style={{
            ...(stretch ? { flex: 1 } : { flexShrink: 0 }), position: 'relative', zIndex: 1, height: 36, padding: '0 14px', borderRadius: 'var(--radius-pill)', cursor: 'pointer',
            border: `1px solid ${on ? 'transparent' : 'var(--border)'}`, background: on ? 'transparent' : 'var(--bg-card)',
            color: on ? 'var(--amber-ink)' : 'var(--text)', fontFamily: 'var(--font)', fontSize: size, fontWeight: 600, whiteSpace: 'nowrap',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'color 200ms, background 160ms',
          }}>
            {o.label}
            {o.badge != null && o.badge !== 0 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, opacity: 0.8 }}>{o.badge}</span>}
          </button>
        )
      })}
    </div>
  )
}

// FilmstripPanel — the directional companion to FilmstripTabs. Wrap the active
// tab's content in it; pass the SAME index-ordered `tabs` array and `value` so it
// can tell which way you moved. Remounts on tab change (key) so the slide-in
// restarts cleanly, same idiom as the app's `cascade-in` tab-content pattern.
export function FilmstripPanel({ tabs, value, children, style }) {
  const opts = tabs.map(t => (typeof t === 'string' ? t : t.value))
  const idxRef = useRef(opts.indexOf(value))
  const dirRef = useRef(1)
  const nextIdx = opts.indexOf(value)
  if (nextIdx !== idxRef.current) { dirRef.current = nextIdx > idxRef.current ? 1 : -1; idxRef.current = nextIdx }
  return (
    <div key={value} style={{
      '--nxa': `${dirRef.current === 1 ? 'filmstripSlideR' : 'filmstripSlideL'} 0.36s cubic-bezier(0.22,1,0.36,1) both`,
      ...style,
    }}>{children}</div>
  )
}

// ── IconTile + ActionRow (17.179) ───────────────────────────────────────────
// The house icon tile and the house "icon · label · sub" action row. Every sheet and
// hub used to hand-roll a flat grey 32px square with a white glyph, so "Open the show
// page" and "Not this one right now" sat side by side with the same arrow (Jason
// 2026-11-02: "improve these icons… colorization with slight animation shifts").
// A tile wears the TONE of what its row does — the vocabulary is TONES below — the
// rows slide in staggered (index), a press squeezes the tile, hover lifts it, and
// only a tile marked `breathe` (the current choice) pulses. CSS lives in index.css
// (.ui-tile / .ui-action-row), reduced-motion safe.
export const TONES = {
  amber:   'var(--amber)',                   // the app's verb: marking, position, where it plays
  social:  'var(--social, var(--amber))',    // people: friends, together, aftershow
  live:    'var(--green)',                   // a live signal: watching now, alerts on
  neutral: 'var(--text)',                    // navigation, info
  muted:   'var(--text-dim)',                // de-emphasised: skip, sign out, later
  danger:  'var(--danger-text)',             // remove, abandon
}
// `drift` (ms, negative offset) staggers the resting idle cycle so tiles never pulse in
// unison; `still` turns it off for places that must sit dead quiet.
export function IconTile({ children, icon, tone = TONES.amber, solid = false, breathe = false, drift = 0, still = false, size = 32, radius = 10, iconSize, style, ...props }) {
  const t = TONES[tone] || tone
  const Icon = icon
  return (
    <span className="ui-tile" aria-hidden="true" {...props} style={{
      width: size, height: size, borderRadius: radius, flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: solid ? t : `color-mix(in srgb, ${t} ${breathe ? 18 : 11}%, var(--surface))`,
      border: solid ? 'none' : `1px solid color-mix(in srgb, ${t} ${breathe ? 45 : 28}%, transparent)`,
      color: solid ? 'var(--amber-ink)' : t,
      '--tone': t,
      '--nxa': breathe ? 'uiTileBreathe 3.4s ease-in-out infinite' : (!still && !solid) ? `uiTileDrift 7.5s ease-in-out ${-Math.abs(drift)}ms infinite` : undefined,
      ...style,
    }}>{Icon ? <Icon size={iconSize || Math.round(size / 2)} /> : children}</span>
  )
}
export function ActionRow({ icon, iconNode, tone = 'amber', solid, label, sub, on, dim, trailing, onClick, index = 0, height = 54, divider = true, disabled, style, ...props }) {
  const t = on ? TONES.amber : dim ? TONES.muted : tone
  return (
    <button onClick={onClick} disabled={disabled} className="ui-action-row" {...props} style={{
      width: '100%', minHeight: height, display: 'flex', alignItems: 'center', gap: 14,
      background: 'none', border: 'none', borderBottom: divider ? '1px solid var(--border-soft, var(--border))' : 'none',
      padding: '8px 0', cursor: disabled ? 'default' : 'pointer', fontFamily: 'var(--font)', textAlign: 'left', color: 'var(--text)',
      '--row-delay': `${40 + index * 38}ms`,
      '--nxa': `uiRowIn 0.42s var(--ease-out) ${40 + index * 38}ms both`, ...style,
    }}>
      <IconTile icon={icon} tone={t} solid={solid} breathe={!!on} drift={index * 1100}>{iconNode}</IconTile>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 15, color: dim ? 'var(--text-muted)' : 'var(--text)', fontWeight: on ? 600 : 400 }}>{label}</span>
        {sub && <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-dim)', marginTop: 2 }}>{sub}</span>}
      </span>
      {on && trailing == null && <Eyebrow size={10}>Current</Eyebrow>}
      {trailing != null && <span style={{ flexShrink: 0, color: 'var(--text-dim)', fontSize: 13 }}>{trailing}</span>}
    </button>
  )
}

// MenuRow — one row of a grouped-in-card menu (1d/rule 4): rows live inside a
// bordered card, never separated by divider stripes. `icon` is a leading glyph/node,
// `danger` tints it the outlined-destructive red, `active` gives the amber tint,
// `trailing` is an optional right-side node (chevron, count, check).
export function MenuRow({ icon, children, onClick, active, danger, trailing, style }) {
  const color = danger ? 'var(--danger-text)' : active ? 'var(--amber)' : 'var(--text)'
  return (
    <button onClick={onClick} className="ui-menu-row" style={{
      display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left',
      background: active ? 'rgba(var(--amber-rgb),0.09)' : 'none', border: 'none',
      padding: '11px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
      color, fontFamily: 'var(--font)', ...style,
    }}>
      {icon != null && <span style={{ width: 18, textAlign: 'center', flexShrink: 0, color: active ? 'var(--amber)' : danger ? 'var(--danger-text)' : 'var(--text-dim)' }}>{icon}</span>}
      <span style={{ flex: 1, fontSize: 14, fontWeight: active ? 600 : 500, minWidth: 0 }}>{children}</span>
      {trailing != null && <span style={{ flexShrink: 0, color: 'var(--text-dim)', fontSize: 13 }}>{trailing}</span>}
    </button>
  )
}

// MenuCard — the bordered container MenuRows group inside (rule 4). Compose
// <MenuCard><MenuRow/>…</MenuCard>, and put destructive rows in their own MenuCard.
export function MenuCard({ children, style }) {
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border-soft)',
      borderRadius: 'var(--radius-md)', padding: 6,
      display: 'flex', flexDirection: 'column', gap: 2, ...style,
    }}>{children}</div>
  )
}

// Dropdown — the canonical anchored menu (2a): a trigger that opens a floating panel
// with an optional header, closing on outside-click or Escape. `trigger` is a render
// prop ({open}) => node; children are the panel contents (compose MenuRow/MenuCard).
// `align` end (default) right-aligns the panel to the trigger; `start` left-aligns.
export function Dropdown({ trigger, header, children, align = 'start', panelWidth = 214, style }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', ...style }}>
      <span onClick={() => setOpen(o => !o)}>{trigger({ open })}</span>
      {open && (
        <div role="menu" style={{
          position: 'absolute', top: 'calc(100% + 7px)', [align === 'end' ? 'right' : 'left']: 0,
          zIndex: 'var(--z-overlay, 60)', width: panelWidth,
          background: 'var(--sheet)',
          border: '1px solid rgba(var(--ink-rgb),0.12)', borderRadius: 'var(--radius-md)', padding: 6,
          boxShadow: '0 18px 44px rgba(0,0,0,0.75), inset 0 1px 0 rgba(var(--ink-rgb),0.06)',
          '--nxa': 'uiPop 0.2s var(--ease-out) both',
        }} onClick={() => setOpen(false)}>
          {header && (
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '6px 8px 8px',
              fontSize: 9.5, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--text-dim)',
            }}>{header}</div>
          )}
          {children}
        </div>
      )}
    </div>
  )
}

// ── Toggle / SettingRow ───────────────────────────────────────────────────────
// The canonical 52x31 reel-knob switch already lives in profile/ProfileHelpers (it
// also handles an async onChange with a busy state). Re-exported here so UI.jsx is
// the ONE import path for the control set — do not fork a second implementation.
export { Toggle, SettingRow } from './profile/ProfileHelpers'

// ── SearchField — the canonical search input ──────────────────────────────────
// The app had NINE hand-rolled search inputs (two using an emoji magnifier, several
// under 16px which zoom-scrolls iOS on focus, sizes and radii all different). This is
// the People/Discover treatment promoted to canon: lucide icon, --surface, 16px, and a
// properly-sized labelled clear button.
// `variant="hero"` keeps Discover's amber focus halo for the primary add-a-show field.
export function SearchField({ value, onChange, onClear, placeholder = 'Search…', label, autoFocus, variant, style }) {
  const [focused, setFocused] = useState(false)
  const hero = variant === 'hero'
  return (
    <div style={{ position: 'relative', width: '100%', ...style }}>
      <Search size={16} aria-hidden="true" style={{
        position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)',
        color: focused ? 'var(--amber)' : 'var(--text-dim)', pointerEvents: 'none',
        transition: 'color 160ms var(--ease-out)',
      }}/>
      <input
        type="search" value={value} autoFocus={autoFocus}
        onChange={e => onChange?.(e.target.value)}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        placeholder={placeholder} aria-label={label || placeholder}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: `11px ${value ? 40 : 14}px 11px 36px`,
          background: 'var(--surface)', color: 'var(--text-strong)',
          border: `1px solid ${focused && hero ? 'rgba(var(--amber-rgb),0.65)' : 'var(--border)'}`,
          borderRadius: 'var(--radius)',
          // 16px is load-bearing: anything smaller makes iOS Safari zoom the page on focus.
          fontSize: 16, fontFamily: 'var(--font)',
          boxShadow: focused && hero ? '0 0 0 3px rgba(var(--amber-rgb),0.16)' : 'none',
          transition: 'border-color 160ms var(--ease-out), box-shadow 160ms var(--ease-out)',
        }}
      />
      {value && (
        <button
          type="button" aria-label="Clear search" className="ui-hit"
          onClick={() => { onClear ? onClear() : onChange?.('') }}
          style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            width: 28, height: 28, borderRadius: 'var(--radius-full)',
            background: 'var(--surface)', border: 'none', color: 'var(--text)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          <X size={14}/>
        </button>
      )}
    </div>
  )
}

// ── BackButton — the canonical header back arrow ──────────────────────────────
// The 34px circular header arrow was re-rolled verbatim in 8+ pages (Search, Cliffhanger,
// Shelves, ShelfDetail, Rewind, Cards, ReadingRoom, Help, Notifications, ImportStart,
// TonightPlansPage, Admin…). It was the single largest "bespoke only because no canonical
// target existed" category in the 2026-07-26 button audit, so this closes it.
// Defaults to history-back; pass `to` for an explicit destination, or `onClick` to override.
// Auto-applies .ui-hit so a 34px control still clears the 44px tap minimum.
export function BackButton({ onClick, to, size = 34, label = 'Go back', variant = 'surface', style }) {
  const navigate = useNavigate()
  const go = onClick || (() => (to ? navigate(to) : navigate(-1)))
  return (
    <button
      onClick={go} aria-label={label} data-tip={label === 'Go back' ? 'Back' : label} data-tip-pos="below"
      className={size < 44 ? 'ui-hit' : undefined}
      style={{
        background: variant === 'overlay' ? 'rgba(0,0,0,0.45)' : 'var(--surface)',
        border: 'none', borderRadius: 'var(--radius-full)',
        width: size, height: size, flexShrink: 0,
        color: variant === 'overlay' ? 'rgb(var(--photo-ink-rgb))' : 'var(--text)', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        ...style,
      }}>
      <ArrowLeft size={Math.round(size * 0.47)}/>
    </button>
  )
}

// ── Dismissible — the standard way a card LEAVES ──────────────────────────────
// A dismissable card that vanishes on the same frame you tap ✕ reads as a glitch: the
// eye can't distinguish "I dismissed that" from "something broke", and everything below
// snaps upward into the gap. Every such card should fade, lift slightly, and roll its
// height closed so the content beneath slides up. (Jason 2026-07-30 — asked for it on the
// Home marquee, then correctly asked for it as a standard rather than a one-off.)
//
// USAGE — the two pieces are separate because the commit must be deferred:
//
//   const [exiting, dismiss] = useDismiss(() => { setGone(true); save() })
//   if (gone) return null
//   return <Dismissible exiting={exiting}><MyCard onClose={dismiss} /></Dismissible>
//
// WHY DEFERRED: committing the dismissal immediately (setting the state that makes the
// card stop rendering) unmounts it before a single frame of animation runs. So `dismiss`
// only flips a local flag; your commit callback fires after the exit finishes. Persist
// to storage inside the callback OR immediately in your handler — but do not remove the
// element from the tree until the callback.
export const DISMISS_MS = 320   // keep in step with --dur-base in index.css

// Returns [exiting, dismiss]. `dismiss` is safe to call twice — the second is ignored.
export function useDismiss(onCommit, ms = DISMISS_MS) {
  const [exiting, setExiting] = useState(false)
  const timer = useRef(null)
  const cb = useRef(onCommit)
  cb.current = onCommit
  // Never leave a timer running past unmount: it would setState on a dead component and,
  // worse, the dismissal would silently never be committed.
  useEffect(() => () => clearTimeout(timer.current), [])
  const dismiss = () => {
    if (timer.current) return
    setExiting(true)
    timer.current = setTimeout(() => { timer.current = null; cb.current?.() }, ms)
  }
  return [exiting, dismiss]
}

// The wrapper. Rolls height closed (.collapse-grid) while the inner card fades and lifts
// (.card-dismiss). Both honour prefers-reduced-motion — reduced motion still gets the
// state change, just without the travel.
export function Dismissible({ exiting, children, style }) {
  return (
    <div className="collapse-grid" data-open={exiting ? 'false' : 'true'}>
      <div>
        <div className="card-dismiss" data-exiting={exiting ? 'true' : 'false'} style={style}>
          {children}
        </div>
      </div>
    </div>
  )
}

// ── Collapse — the default for ANYTHING that shows/hides in place ─────────────
// Wraps .collapse-grid: animates real height 0↔1fr in BOTH directions (a keyframe
// can't), so expanding and collapsing are equally smooth — never an instant jar.
// The inner content also cross-fades, so it reads as settling into view rather
// than popping. Reach for this instead of `{open && <div>...}` any time a panel,
// accordion, or "show more" section toggles — the Episode Ratings Heatmap is the
// first adopter. (Jason 2026-08-06: generalized from that toggle so nothing else
// has to hand-roll the same fade/roll-up again.)
export function Collapse({ open, children, style }) {
  return (
    <div className="collapse-grid" data-open={open ? 'true' : 'false'}>
      <div>
        {/* .collapse-inner drives the fade + settle (lift into place) from CSS so it
            reads as content arriving rather than popping, and so prefers-reduced-motion
            can switch it all off. Opening is gentle and slightly delayed (it settles in
            as the panel finishes rolling open); closing is quicker (Jason 2026-08-11). */}
        <div className="collapse-inner" style={style}>
          {children}
        </div>
      </div>
    </div>
  )
}

// ── Sheet — the canonical overlay ─────────────────────────────────────────────
// Portal + backdrop + accessible dialog behaviour in ONE wrapper.
//
// WHY THIS EXISTS. `useDialog` already did the hard part (focus trap, Escape,
// scroll lock, focus restore) and was correct — but only 8 of the app's 49
// portal components used it. The other 41 hand-rolled an overlay, and the result
// wasn't just duplication, it was 49 surfaces that BEHAVE DIFFERENTLY. Measured
// 2026-08-01:
//
//     handle Escape ............  7 / 49   (14%)
//     lock background scroll ...  4 / 49   (8%)
//     close on backdrop tap .... 25 / 49   (51%)
//
// So on most sheets Escape does nothing and the page scrolls away underneath you
// — the exact bug already fixed once by hand in the Viewing Room. The hook wasn't
// being skipped out of laziness so much as economics: hand-rolling was FEWER
// steps than importing a hook and remembering role/aria-modal/aria-label. This
// makes the correct path the lazy path.
//
// ADOPT INCREMENTALLY. Every existing sheet keeps working untouched; convert one
// when you're already in the file. `from` matches the three motions the app
// already uses, so conversion is usually a zero-visual-change diff:
//   bottom — the default; choosing/confirming (SheetSelect, ConfirmSheet)
//   right  — a panel of tools (ShowActionsDrawer)
//   center — a focused modal
//
// Pass `panelStyle` to keep a sheet's existing skin. `dismissible={false}` for a
// flow that must be answered rather than dismissed (backdrop tap + Escape off).
// The open/closed gate lives in this OUTER component, on purpose. useDialog is a hook,
// so it can't sit behind an `if (!open) return null` — a closed <Sheet> would still run
// it, meaning any page merely RENDERING a closed sheet would lock its own scroll and
// never release it. (Caught immediately after writing it: PageHelp renders its sheet on
// every page.) Splitting means the dialog behaviour mounts only when the sheet does.
export function Sheet({ open = true, ...props }) {
  if (!open) return null
  return <SheetPanel {...props} />
}

function SheetPanel({
  onClose, label, from = 'bottom', width, z = 'var(--z-sheet)',
  dismissible = true, backdrop = true, panelStyle, style, children, rootProps = {},
  handle = true, closeButton = false,
}) {
  const ref = useDialog(dismissible ? onClose : undefined)
  // Swipe-down to dismiss (2026-10-18, Jason: "this sheet cannot be closed"). A tall
  // sheet leaves no backdrop to tap, so the handle is a real grab: the panel follows
  // the finger, and a release past 90px (or a fast flick) closes it. Pointer events
  // only on the handle strip, so the rows' own taps and scrolls are untouched.
  const dragY = useRef(0); const startY = useRef(null); const startT = useRef(0)
  const onHandleDown = (e) => { if (!dismissible || from !== 'bottom') return; startY.current = e.clientY; startT.current = Date.now(); e.currentTarget.setPointerCapture?.(e.pointerId) }
  const onHandleMove = (e) => { if (startY.current == null) return; dragY.current = Math.max(0, e.clientY - startY.current); if (ref.current) { ref.current.style.transform = `translateY(${dragY.current}px)`; ref.current.style.transition = 'none' } }
  const onHandleUp = () => {
    if (startY.current == null) return
    const dy = dragY.current, dt = Date.now() - startT.current
    startY.current = null; dragY.current = 0
    if (ref.current) { ref.current.style.transition = 'transform .22s var(--ease-out)'; ref.current.style.transform = '' }
    if (dy > 90 || (dy > 30 && dt < 220)) onClose?.()
  }

  const align = from === 'bottom' ? 'flex-end' : from === 'right' ? 'flex-start' : 'center'
  const justify = from === 'right' ? 'flex-end' : 'center'
  const anim = from === 'bottom' ? 'uiSheetUp' : from === 'right' ? 'uiSheetIn' : 'uiSheetPop'

  const base = from === 'bottom'
    ? { width: '100%', maxWidth: width || 460, borderRadius: '20px 20px 0 0',
        padding: '20px 18px calc(24px + env(safe-area-inset-bottom))' }
    : from === 'right'
    ? { width: width || 'min(320px, 86vw)', height: '100%', borderLeft: '1px solid var(--border)',
        padding: 'max(14px, env(safe-area-inset-top)) 0 max(14px, env(safe-area-inset-bottom))' }
    : { width: '100%', maxWidth: width || 420, borderRadius: 'var(--radius-lg)', padding: '20px 18px' }

  return createPortal((
    <div
      {...rootProps}
      onClick={dismissible ? onClose : undefined}
      style={{
        position: 'fixed', inset: 0, zIndex: z, fontFamily: 'var(--font)',
        background: backdrop ? 'rgba(0,0,0,0.66)' : 'transparent',
        backdropFilter: backdrop ? 'blur(5px)' : undefined,
        WebkitBackdropFilter: backdrop ? 'blur(5px)' : undefined,
        ...style,
      }}>
      {/* Backdrop covers the viewport; the panel is confined to the app column. */}
      <div className="ui-sheet-col" style={{ alignItems: align, justifyContent: justify }}>
        <div
          ref={ref} role="dialog" aria-modal="true" aria-label={label}
          onClick={e => e.stopPropagation()}
          style={{
            background: 'var(--sheet)', border: '1px solid var(--border)',
            '--nxa': `${anim} var(--dur-base, 0.28s) var(--ease-out) both`,
            maxHeight: from === 'right' ? '100%' : '92vh', overflowY: 'auto', position: 'relative',
            ...base, ...panelStyle,
          }}>
          {from === 'bottom' && handle && dismissible && (
            <div onPointerDown={onHandleDown} onPointerMove={onHandleMove} onPointerUp={onHandleUp} onPointerCancel={onHandleUp}
              aria-label="Drag down to close" role="presentation"
              style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 28, touchAction: 'none', cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ width: 36, height: 4, borderRadius: 999, background: 'var(--border)' }} />
            </div>
          )}
          {closeButton && dismissible && (
            <button type="button" onClick={onClose} aria-label="Close" className="ui-hit" style={{ position: 'absolute', top: 10, right: 10, width: 32, height: 32, borderRadius: 'var(--radius-full)', background: 'var(--surface)', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, zIndex: 1 }}>×</button>
          )}
          {children}
        </div>
      </div>
    </div>
  ), document.body)
}
